import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Footer from '../components/Footer';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://templimizeai-coursecreatorvault.onrender.com/api';

interface EmailInSequence {
  day: number;
  subject: string;
  previewText: string;
  purpose: string;
  body: string;
  callToAction: string;
}

interface EmailSequence {
  emails: EmailInSequence[];
  sequenceType: string;
  courseName: string;
}

const EmailSequenceGenerator: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const { token, user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [course, setCourse] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sequence, setSequence] = useState<EmailSequence | null>(null);
  const [sequenceType, setSequenceType] = useState<'launch' | 'welcome' | 'nurture'>('launch');

  useEffect(() => {
    fetchCourse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API_URL}/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const foundCourse = response.data.courses.find((c: any) => c.id === courseId);
      if (foundCourse) {
        setCourse(foundCourse);
      } else {
        toast.error('Course not found');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const generateSequence = async () => {
    if (!course) return;

    if (user?.tier === 'free') {
      toast.error('Please upgrade to Basic or Premium to generate email sequences');
      navigate('/pricing');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/ai/generate-email-sequence`,
        {
          courseTitle: course.title,
          courseDescription: course.description,
          targetAudience: course.targetAudience || '',
          sequenceType
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSequence(response.data.sequence);
      toast.success('Email sequence generated!');
    } catch (error: any) {
      console.error('Error generating sequence:', error);
      toast.error(error.response?.data?.error || 'Failed to generate sequence');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const copyAllEmails = () => {
    if (!sequence) return;
    const allContent = sequence.emails.map(email =>
      `--- EMAIL ${email.day} (Day ${email.day}) ---\n` +
      `Subject: ${email.subject}\n` +
      `Preview: ${email.previewText}\n` +
      `Purpose: ${email.purpose}\n\n` +
      `${email.body}\n\n` +
      `CTA: ${email.callToAction}\n`
    ).join('\n\n');
    copyToClipboard(allContent);
  };

  const exportToCSV = () => {
    if (!sequence) return;
    const rows = [
      ['Day', 'Subject', 'Preview Text', 'Purpose', 'Body', 'Call to Action'],
      ...sequence.emails.map(email => [
        String(email.day),
        email.subject,
        email.previewText,
        email.purpose,
        email.body,
        email.callToAction
      ])
    ];
    const csvContent = rows.map(row =>
      row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${course.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_email_sequence.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('CSV exported! Import this into Mailchimp, ConvertKit, or any email tool.');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`/marketing/${courseId}`)}
                className="text-gray-600 hover:text-gray-900"
              >
                ← Back to Marketing
              </button>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  The Course Creator's Vault
                </h1>
                <p className="text-xs text-gray-500">by Templimize AI</p>
              </div>
            </div>
            <div className="text-sm text-gray-600">{user?.name}</div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-4xl font-bold mb-4">📧 Email Sequence Generator</h2>
          <p className="text-blue-100 text-lg mb-2">
            For: <strong>{course?.title}</strong>
          </p>
          <p className="text-blue-200">
            Generate complete email sequences to launch and sell your course!
          </p>
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="text-2xl mr-2">💡</span> How This Works
          </h3>
          <div className="bg-blue-50 rounded-lg p-4 mb-4">
            <p className="text-gray-700 mb-3">
              This tool generates a <strong>complete email sequence</strong> that you can copy into your email marketing tool (Mailchimp, ConvertKit, ActiveCampaign, etc.).
            </p>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="bg-white rounded-lg p-3 border border-blue-200">
                <div className="font-bold text-blue-600 mb-1">Step 1</div>
                <div className="text-gray-600">Choose sequence type & generate</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-blue-200">
                <div className="font-bold text-blue-600 mb-1">Step 2</div>
                <div className="text-gray-600">Copy emails or export to CSV</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-blue-200">
                <div className="font-bold text-blue-600 mb-1">Step 3</div>
                <div className="text-gray-600">Paste into your email tool & schedule</div>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-500">
            <strong>Note:</strong> You'll send these emails through YOUR email provider (Mailchimp, ConvertKit, etc.) to YOUR subscribers. We generate the content - you control the sending!
          </p>
        </div>

        {/* Sequence Type Selection */}
        {!sequence && (
          <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Choose Your Sequence Type</h3>

            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <button
                onClick={() => setSequenceType('launch')}
                className={`p-6 rounded-xl border-2 text-left transition ${
                  sequenceType === 'launch'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-3xl mb-2">🚀</div>
                <div className="font-bold text-gray-900">Launch Sequence</div>
                <div className="text-sm text-gray-600 mt-1">
                  7-email sequence to launch and sell your course
                </div>
                <div className="text-xs text-blue-600 mt-2 font-medium">
                  Best for: New course launches
                </div>
              </button>

              <button
                onClick={() => setSequenceType('welcome')}
                className={`p-6 rounded-xl border-2 text-left transition ${
                  sequenceType === 'welcome'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-3xl mb-2">👋</div>
                <div className="font-bold text-gray-900">Welcome Sequence</div>
                <div className="text-sm text-gray-600 mt-1">
                  5-email sequence for new subscribers
                </div>
                <div className="text-xs text-green-600 mt-2 font-medium">
                  Best for: Building relationships
                </div>
              </button>

              <button
                onClick={() => setSequenceType('nurture')}
                className={`p-6 rounded-xl border-2 text-left transition ${
                  sequenceType === 'nurture'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-3xl mb-2">🌱</div>
                <div className="font-bold text-gray-900">Nurture Sequence</div>
                <div className="text-sm text-gray-600 mt-1">
                  10-email value sequence with soft sells
                </div>
                <div className="text-xs text-purple-600 mt-2 font-medium">
                  Best for: Warming up cold leads
                </div>
              </button>
            </div>

            <button
              onClick={generateSequence}
              disabled={generating}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-xl transition disabled:opacity-50"
            >
              {generating ? (
                <span className="flex items-center justify-center">
                  <span className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin mr-3"></span>
                  Generating Your Email Sequence...
                </span>
              ) : (
                `✨ Generate ${sequenceType === 'launch' ? '7-Email Launch' : sequenceType === 'welcome' ? '5-Email Welcome' : '10-Email Nurture'} Sequence`
              )}
            </button>
          </div>
        )}

        {/* Generated Sequence */}
        {sequence && (
          <>
            {/* Action Buttons */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8">
              <div className="flex flex-wrap gap-4 justify-between items-center">
                <div>
                  <h3 className="font-bold text-gray-900">{sequence.emails.length} Emails Generated</h3>
                  <p className="text-sm text-gray-600">Copy individually or export all at once</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={copyAllEmails}
                    className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition"
                  >
                    📋 Copy All
                  </button>
                  <button
                    onClick={exportToCSV}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition"
                  >
                    📥 Export CSV
                  </button>
                  <button
                    onClick={() => setSequence(null)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition"
                  >
                    🔄 New Sequence
                  </button>
                </div>
              </div>
            </div>

            {/* Email List */}
            <div className="space-y-6">
              {sequence.emails.map((email, idx) => (
                <div key={idx} className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 flex justify-between items-center">
                    <div>
                      <div className="font-bold text-lg">Email {idx + 1} - Day {email.day}</div>
                      <div className="text-blue-200 text-sm">{email.purpose}</div>
                    </div>
                    <button
                      onClick={() => copyToClipboard(
                        `Subject: ${email.subject}\nPreview: ${email.previewText}\n\n${email.body}\n\n${email.callToAction}`
                      )}
                      className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg font-medium transition"
                    >
                      📋 Copy
                    </button>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Subject Line</div>
                        <div className="bg-gray-50 p-3 rounded-lg font-medium text-gray-900">{email.subject}</div>
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Preview Text</div>
                        <div className="bg-gray-50 p-3 rounded-lg text-gray-700">{email.previewText}</div>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Email Body</div>
                      <div className="bg-gray-50 p-4 rounded-lg text-gray-700 whitespace-pre-wrap">{email.body}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Call to Action</div>
                      <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-blue-700 font-medium">{email.callToAction}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Next Steps */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6 mt-8">
              <h3 className="font-bold text-green-800 text-lg mb-3">📬 Next Steps: How to Use These Emails</h3>
              <ol className="list-decimal list-inside space-y-2 text-green-700">
                <li>Click <strong>"Export CSV"</strong> to download all emails in a spreadsheet format</li>
                <li>Open your email marketing tool (Mailchimp, ConvertKit, ActiveCampaign, etc.)</li>
                <li>Create a new automation/sequence and import or paste each email</li>
                <li>Set the timing (e.g., Day 1, Day 3, Day 5) based on the day numbers</li>
                <li>Customize the emails with your personal touch and branding</li>
                <li>Connect your landing page or lead magnet to trigger the sequence</li>
                <li>Test with yourself first, then go live!</li>
              </ol>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default EmailSequenceGenerator;
